import { DeleteOutlined, EditOutlined, EyeOutlined, UserOutlined } from '@ant-design/icons';
import { App, Button, Card, Descriptions, DescriptionsProps, Popconfirm } from 'antd';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { btlApplicationFormApi } from '../../../../api/btlApplicationForm';
import { Individual } from '../../../../api/btlApplicationForm/types';
import { PageURL } from '../../../../router/pageURL';
import { TypedFormatMessageFn } from '../../../../types/intl';
import { BtlApplicationFormPageEnum } from '../../types';
import { updateUrl } from '../Applicants';

export const IndividualDescription = ({
  individual,
  variant = 'btl',
}: {
  individual: Individual;
  variant?: 'btl' | 'oo';
}) => {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const email = individual.contactDetails?.find(contact => contact.contactType === 'EMAIL')?.contact;
  const phoneNumber = individual.contactDetails?.find(contact => contact.contactType === 'PHONE')?.contact;

  const individualDetails: DescriptionsProps['items'] = [
    ...(variant === 'btl'
      ? [
          {
            label: formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.email.label' }),
            children: email,
            span: 3,
          },
        ]
      : []),
    {
      label: formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.phoneNumber.label' }),
      children: phoneNumber,
      span: 3,
    },
    {
      label: formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.addressDetails.label' }),
      children: (
        <>
          {Object.entries(individual.addressHistory.length && individual.addressHistory?.[0]?.address)
            .filter(([key, value]) => key !== 'id' && value)
            .map(([key, value]) => (
              <div key={key}>{`${value}`}</div>
            ))}
        </>
      ),
    },
  ];

  return (
    <Descriptions
      styles={{ label: { textAlign: 'end', alignContent: 'start' } }}
      colon
      items={individualDetails}
      bordered={true}
      layout={'horizontal'}
      size={'middle'}
    />
  );
};

export const IndividualCard = ({
  individual,
  applicationId,
  borrowerId,
  showRemoveAction,
  refetch,
  isEditable,
  onViewIndividual,
  listVariant = 'btl',
}: {
  individual: Individual;
  applicationId: string;
  borrowerId: string;
  showRemoveAction: boolean;
  refetch: () => void;
  isEditable: boolean;
  onViewIndividual?: (individualId: string) => void;
  listVariant?: 'btl' | 'oo';
}) => {
  const { message } = App.useApp();
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const navigate = useNavigate();

  return (
    <Card
      title={
        <>
          <div className='flex flex-row justify-between gap-4'>
            <span>
              <UserOutlined /> {individual.title} {individual.firstName} {individual.lastName}
            </span>
            <span className='opacity-50 text-sm my-auto'>{individual.isPrimary ? 'Primary' : ''}</span>
          </div>
        </>
      }
      actions={[
        <Button
          key='actionIndividual'
          className={'w-full'}
          type={'link'}
          onClick={() => {
            if (onViewIndividual) {
              onViewIndividual(individual.id);
              return;
            }

            updateUrl(PageURL.BTL_APPLICATION_EDIT, BtlApplicationFormPageEnum.APPLICANTS_INDIVIDUAL, navigate, {
              applicationId,
              individualId: individual.id,
            });
          }}
          icon={isEditable ? <EditOutlined /> : <EyeOutlined />}
        >
          {formatMessage({
            id: isEditable
              ? 'btlApplicationForm.APPLICANTS_INDIVIDUAL.editIndividual.label'
              : 'btlApplicationForm.APPLICANTS_INDIVIDUAL.viewIndividual.label',
          })}
        </Button>,
        showRemoveAction && isEditable ? (
          <Popconfirm
            key='removeIndividual'
            title={formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.removeIndividual.label' })}
            description={formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.removeIndividual.prompt' })}
            onConfirm={async () => {
              try {
                await btlApplicationFormApi.deleteIndividual(applicationId, borrowerId as string, individual.id);
                refetch();
                message.success(
                  formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.removeIndividual.success' }),
                );
              } catch (e: any) {
                message.error(e.message);
              }
            }}
            okText={formatMessage({ id: 'common.yes' })}
            cancelText={formatMessage({ id: 'common.no' })}
          >
            <Button className='w-full' type={'link'} danger icon={<DeleteOutlined />}>
              {formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.removeIndividual.label' })}
            </Button>
          </Popconfirm>
        ) : null,
      ].filter(val => val)}
    >
      <IndividualDescription individual={individual} variant={listVariant} />
    </Card>
  );
};
